import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AutoAwesome, DeleteOutline } from '@mui/icons-material';
import { TEMPLATE_FIELD_KINDS, TEMPLATE_FIELD_SOURCES } from '../../utils/templateFields';
import { FIELD_KIND_LABELS, FIELD_SOURCE_LABELS } from './fieldLabels';

/**
 * Edit popover for a template field: instruction, output kind, source, required, max words.
 * Changes are committed once when the popover closes so typing does not create one
 * transaction per keystroke.
 *
 * @param {object} props - Component props.
 * @param {HTMLElement|null} props.anchorEl - Element to anchor to.
 * @param {boolean} props.open - Whether the popover is open.
 * @param {{instruction: string, kind: string, required: boolean, source: string, maxWords: number|null}} props.attrs - Current field attributes.
 * @param {boolean} props.isBlock - Whether the field is a block field.
 * @param {(attrs: object) => void} props.onCommit - Called with changed attributes on close.
 * @param {() => void} props.onDelete - Delete the field.
 * @param {(toBlock: boolean) => void} [props.onConvert] - Switch between a section and an inline field.
 * @param {() => void} props.onClose - Close the popover.
 * @returns {JSX.Element}
 */
const TemplateFieldPopover = ({ anchorEl, open, attrs, isBlock, onCommit, onDelete, onConvert, onClose }) => {
  const [draft, setDraft] = useState(attrs);
  const instructionRef = useRef(null);

  useEffect(() => {
    if (open) setDraft(attrs);
  }, [open, attrs]);

  /**
   * Commit changed values (if any) and close.
   * @returns {void}
   */
  const commitDraft = () => {
    const maxWords = parseInt(draft.maxWords, 10);
    const next = {
      instruction: String(draft.instruction || '').replace(/\s+/g, ' ').trim(),
      kind: draft.kind,
      required: draft.required !== false,
      source: draft.source,
      maxWords: Number.isFinite(maxWords) && maxWords > 0 ? maxWords : null,
    };
    const changed = Object.keys(next).some((key) => next[key] !== attrs[key]);
    if (changed) onCommit(next);
  };

  const handleClose = () => {
    commitDraft();
    onClose();
  };

  /**
   * Save the draft, then switch the field's flavour (the node is replaced, so the popover closes).
   * @param {React.MouseEvent} _event - Toggle event.
   * @param {'block'|'inline'|null} value - Chosen flavour.
   * @returns {void}
   */
  const handleShape = (_event, value) => {
    if (!value || !onConvert || value === (isBlock ? 'block' : 'inline')) return;
    commitDraft();
    onClose();
    onConvert(value === 'block');
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      TransitionProps={{ onEntered: () => instructionRef.current?.focus() }}
      PaperProps={{
        sx: {
          p: 2,
          width: 380,
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: 'var(--q-radius-lg, 12px)',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Stack spacing={1.5} component="form" onSubmit={(e) => { e.preventDefault(); handleClose(); }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesome fontSize="small" sx={{ color: 'var(--q-accent, #4F3BD6)' }} />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {isBlock ? 'AI field (section)' : 'AI field (inline)'}
          </Typography>
        </Stack>
        {onConvert && (
          <Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={isBlock ? 'block' : 'inline'}
              onChange={handleShape}
              aria-label="Field shape"
            >
              <ToggleButton value="block" sx={{ textTransform: 'none', py: 0.25 }}>Section</ToggleButton>
              <ToggleButton value="inline" sx={{ textTransform: 'none', py: 0.25 }}>Inside a sentence</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
              {isBlock
                ? 'Fills its own section: paragraphs, lists or table rows.'
                : 'Fills a short value inside the surrounding text (plain text).'}
            </Typography>
          </Box>
        )}
        <TextField
          inputRef={instructionRef}
          label="Instruction for the AI"
          placeholder="e.g. Summarize the project goals in 2-3 paragraphs"
          multiline
          minRows={2}
          maxRows={8}
          size="small"
          value={draft.instruction || ''}
          onChange={(e) => setDraft((d) => ({ ...d, instruction: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleClose();
            }
          }}
          fullWidth
        />
        <Stack direction="row" spacing={1}>
          <FormControl size="small" fullWidth>
            <InputLabel id="tpl-field-kind">Output</InputLabel>
            <Select
              labelId="tpl-field-kind"
              label="Output"
              value={draft.kind || (isBlock ? 'rich' : 'text')}
              onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value }))}
            >
              {TEMPLATE_FIELD_KINDS.map((kind) => (
                <MenuItem key={kind} value={kind}>{FIELD_KIND_LABELS[kind]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="tpl-field-source">Source</InputLabel>
            <Select
              labelId="tpl-field-source"
              label="Source"
              value={draft.source || 'any'}
              onChange={(e) => setDraft((d) => ({ ...d, source: e.target.value }))}
            >
              {TEMPLATE_FIELD_SOURCES.map((source) => (
                <MenuItem key={source} value={source}>{FIELD_SOURCE_LABELS[source]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Max words"
            type="number"
            size="small"
            value={draft.maxWords ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, maxWords: e.target.value }))}
            inputProps={{ min: 1, max: 5000 }}
            sx={{ width: 130 }}
          />
          <FormControlLabel
            sx={{ ml: 1 }}
            control={(
              <Switch
                size="small"
                checked={draft.required !== false}
                onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
              />
            )}
            label={<Typography variant="body2">Required</Typography>}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Required fields that cannot be filled from the chosen source are marked "Needs input" in the generated document.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={() => {
              onClose();
              onDelete();
            }}
          >
            Delete field
          </Button>
          <Button size="small" variant="contained" type="submit" disableElevation>
            Done
          </Button>
        </Box>
      </Stack>
    </Popover>
  );
};

export default TemplateFieldPopover;
